import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import type { VoiceOption, VoiceOptions } from '../services/settingsTypes';
import { voiceOptionsService } from '../services/voiceOptionsService';
import { VoiceEditModal } from './VoiceEditModal';
import { VoiceRow } from './VoiceRow';

export const VoicesManager = () => {
    const [options, setOptions] = useState<VoiceOptions>({ voices: [], defaultVoiceId: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // undefined: no modal. null: a new voice.
    const [editingVoice, setEditingVoice] = useState<VoiceOption | null | undefined>(undefined);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [generatingVoiceId, setGeneratingVoiceId] = useState<string | null>(null);

    const fetchOptions = async () => {
        try {
            setError(null);
            setOptions(await voiceOptionsService.getVoiceOptions());
        } catch (err) {
            console.error('Failed to fetch voices:', err);
            setError('Failed to load voices');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void fetchOptions();
    }, []);

    /** Saves the options. On failure, shows the error and loads the saved options again. */
    const save = async (next: VoiceOptions): Promise<boolean> => {
        try {
            setError(null);
            await voiceOptionsService.saveVoiceOptions(next);
            setOptions(next);
            return true;
        } catch (err) {
            console.error('Failed to save voices:', err);
            setError(err instanceof Error ? err.message : 'Failed to save voices');
            await fetchOptions();
            return false;
        }
    };

    const replaceVoice = (voices: VoiceOption[], voice: VoiceOption) =>
        voices.map((v) => (v.id === voice.id ? voice : v));

    const handleSaveVoice = async (voice: VoiceOption) => {
        const old = options.voices.find((v) => v.id === voice.id);
        if (editingVoice === null && old) throw new Error(`A voice with ID "${voice.id}" already exists`);

        const voices = old ? replaceVoice(options.voices, voice) : [...options.voices, voice];
        // The first voice becomes the default.
        const defaultVoiceId = options.defaultVoiceId || voice.id;
        // Throw, so the modal stays open and shows the error.
        await voiceOptionsService.saveVoiceOptions({ voices, defaultVoiceId });
        setOptions({ voices, defaultVoiceId });
        setEditingVoice(undefined);
        if (old && old.previewPath !== voice.previewPath) await voiceOptionsService.deletePreview(old.previewPath);
    };

    const handleDelete = async (voice: VoiceOption) => {
        const isDefault = voice.id === options.defaultVoiceId;
        const others = options.voices.filter((v) => v.id !== voice.id);
        if (isDefault && others.length > 0) {
            setError('Make another voice the default before you delete this one.');
            return;
        }
        if (!confirm(`Delete "${voice.name}"? Users who chose it get the default voice.`)) return;

        if (await save({ voices: others, defaultVoiceId: isDefault ? '' : options.defaultVoiceId })) {
            await voiceOptionsService.deletePreview(voice.previewPath);
        }
    };

    const handleMakeDefault = async (voice: VoiceOption) => {
        await save({ ...options, defaultVoiceId: voice.id });
    };

    const handleGeneratePreview = async (voice: VoiceOption) => {
        setGeneratingVoiceId(voice.id);
        try {
            setError(null);
            const previewPath = await voiceOptionsService.generatePreview(voice);
            const updated = { ...voice, previewPath };
            if (await save({ ...options, voices: replaceVoice(options.voices, updated) })) {
                await voiceOptionsService.deletePreview(voice.previewPath);
            }
        } catch (err) {
            console.error('Failed to generate voice preview:', err);
            setError(err instanceof Error ? err.message : 'Failed to generate voice preview');
        } finally {
            setGeneratingVoiceId(null);
        }
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const voices = [...options.voices];
        const [dragged] = voices.splice(draggedIndex, 1);
        voices.splice(index, 0, dragged);
        setOptions({ ...options, voices });
        setDraggedIndex(index);
    };

    const handleDragEnd = async () => {
        if (draggedIndex === null) return;
        setDraggedIndex(null);
        await save(options);
    };

    if (isLoading) {
        return <div className="text-gray-600">Loading voices...</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-start gap-4">
                <div>
                    <h2 className="text-lg font-bold text-gray-900">Voice Mode Voices</h2>
                    <p className="text-sm text-gray-500">
                        Pro users choose one of the published voices (up to 6) in Voice settings, in this order. Users who did not choose a voice,
                        or whose voice is not published any more, get the default voice. Changes apply from the next call.
                    </p>
                </div>
                <Button variant="primary" size="sm" onClick={() => setEditingVoice(null)}>
                    Add Voice
                </Button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {options.voices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    No voices. Voice mode calls fail until you add and publish a voice.
                </div>
            ) : (
                <div className="space-y-2">
                    {options.voices.map((voice, index) => (
                        <VoiceRow
                            key={voice.id}
                            voice={voice}
                            index={index}
                            isDefault={voice.id === options.defaultVoiceId}
                            isDragging={draggedIndex === index}
                            isGeneratingPreview={generatingVoiceId === voice.id}
                            onDragStart={setDraggedIndex}
                            onDragOver={handleDragOver}
                            onDragEnd={() => void handleDragEnd()}
                            onGeneratePreview={(v) => void handleGeneratePreview(v)}
                            onMakeDefault={(v) => void handleMakeDefault(v)}
                            onEdit={setEditingVoice}
                            onDelete={(v) => void handleDelete(v)}
                        />
                    ))}
                </div>
            )}

            {editingVoice !== undefined && (
                <VoiceEditModal
                    voice={editingVoice}
                    onSave={handleSaveVoice}
                    onClose={() => setEditingVoice(undefined)}
                />
            )}
        </div>
    );
};
